// The cosmetic catalog: what elmas (the single soft currency) buys. Pure data + light resolvers, no
// runtime state and no engine coupling, so it is safe to import from anywhere. Cosmetic-only: nothing
// here touches match balance (see docs/META-ECONOMY.md, section 0).
//
// Frames are PROCEDURAL (a CSS class per id, 15 of them, two ANIMATED via CSS keyframes: Infernal
// flickers, Tidecaller flows), so they need no generated art and ship in Tier-1. The 5 default avatars
// reuse the existing race head sprites (sprites/head_<race>.png). The locked avatar variants
// (male/female per race) and the 500-elmas unit sets now have generated art (public/avatars, public/skins),
// so they are all purchasable (the `comingSoon` flag exists for future pending art but nothing uses it now).
// Prices follow the ladder in docs/META-ECONOMY.md: 50/100 cheap, 250/300 mid, 400/500 premium, 500 unit
// sets. Priced by desirability/prestige, never by power.

export type CosmeticKind = 'frame' | 'avatar' | 'set';

export type RaceKey = 'orc' | 'elf' | 'dwarf' | 'human' | 'undead';
export const RACE_KEYS: readonly RaceKey[] = ['orc', 'elf', 'dwarf', 'human', 'undead'];

/** A live Canvas2D elemental effect (the premium frame tier). Rendered by src/ui/effectFrame.ts. */
export type FrameEffect = 'flame' | 'water' | 'ice' | 'nature' | 'arcane' | 'sun' | 'storm' | 'duality';

/** Frame visual tier: flat = coloured ring only (cheap), glow = ring + glow (mid), effect = live canvas (premium). */
export type FrameTier = 'flat' | 'glow' | 'effect';

export interface CatalogItem {
  id: string;
  kind: CosmeticKind;
  name: string;
  /** Cost in elmas. 0 = owned from the start (the default frame + the 5 default avatars). */
  price: number;
  /** Avatars and unit sets are race-themed; frames are race-agnostic. */
  race?: RaceKey;
  /** Frames only: which visual tier this frame belongs to (drives price banding + shop grouping). */
  tier?: FrameTier;
  /** Frames only: the live canvas element for `tier: 'effect'` frames (fire / water / ice / nature). */
  effect?: FrameEffect;
  /** Listed in the shop but not yet purchasable because its art is pending (avatars/sets). */
  comingSoon?: boolean;
}

// --- Frames: three visual tiers, priced by effort/prestige (never power). A long cosmetic tail so the
// economy is never "solved" in a week. Listed cheap -> premium (the shop grid inherits this order).
//   tier 'flat'   : just a coloured ring, no glow          (cheapest)
//   tier 'glow'   : coloured ring + outer glow             (mid)
//   tier 'effect' : a real live Canvas2D element animation (premium; src/ui/effectFrame.ts)
// Each element has BOTH a cheap glow frame and a premium animated frame (ember/flame, frost/ice,
// meadow/nature), so players can chase the "real" version. ---
export const FRAMES: readonly CatalogItem[] = [
  // flat: colour-only rings, ONE flat price (ordinary items, so uniform). Standard is the free default.
  { id: 'fr_default', kind: 'frame', name: 'Standard', price: 0, tier: 'flat' },
  { id: 'fr_bronze', kind: 'frame', name: 'Bronze', price: 50, tier: 'flat' },
  { id: 'fr_silver', kind: 'frame', name: 'Silver', price: 50, tier: 'flat' },
  { id: 'fr_carved', kind: 'frame', name: 'Oakwood', price: 50, tier: 'flat' },
  { id: 'fr_crimson', kind: 'frame', name: 'Crimson', price: 50, tier: 'flat' },
  { id: 'fr_iron', kind: 'frame', name: 'Ironclad', price: 50, tier: 'flat' },
  // glow: coloured ring + glow, ONE price, each a DISTINCT colour (green / gold / orange / cyan / purple)
  { id: 'fr_grass', kind: 'frame', name: 'Meadow', price: 150, tier: 'glow' },
  { id: 'fr_gilded', kind: 'frame', name: 'Gilded', price: 150, tier: 'glow' },
  { id: 'fr_ember', kind: 'frame', name: 'Emberforged', price: 150, tier: 'glow' },
  { id: 'fr_frost', kind: 'frame', name: 'Frostbound', price: 150, tier: 'glow' },
  { id: 'fr_dragon', kind: 'frame', name: 'Dragonscale', price: 150, tier: 'glow' },
  // effect: live canvas animations (premium, special designs so prices vary)
  { id: 'fr_flame', kind: 'frame', name: 'Infernal', price: 550, tier: 'effect', effect: 'flame' },
  { id: 'fr_water', kind: 'frame', name: 'Tidecaller', price: 600, tier: 'effect', effect: 'water' },
  { id: 'fr_ice', kind: 'frame', name: 'Glacial', price: 650, tier: 'effect', effect: 'ice' },
  { id: 'fr_nature', kind: 'frame', name: 'Wildbloom', price: 700, tier: 'effect', effect: 'nature' },
  { id: 'fr_royal', kind: 'frame', name: 'Royal Sigil', price: 750, tier: 'effect', effect: 'arcane' },
  { id: 'fr_sun', kind: 'frame', name: 'Solar', price: 800, tier: 'effect', effect: 'sun' },
  { id: 'fr_storm', kind: 'frame', name: 'Tempest', price: 850, tier: 'effect', effect: 'storm' },
  { id: 'fr_duality', kind: 'frame', name: 'Maelstrom', price: 900, tier: 'effect', effect: 'duality' },
];

// Per-race display names for the themed avatars (matches RACES in src/game/factions.ts).
const RACE_NAME: Record<RaceKey, string> = { orc: 'Orc', elf: 'Elf', dwarf: 'Dwarf', human: 'Human', undead: 'Undead' };

// --- Avatars: 5 defaults (free, reuse race head sprites) + male/female locked variants (art pending) ---
export const AVATARS: readonly CatalogItem[] = RACE_KEYS.flatMap((race): CatalogItem[] => [
  { id: `av_${race}`, kind: 'avatar', name: `${RACE_NAME[race]} Chief`, price: 0, race },
  { id: `av_${race}_m`, kind: 'avatar', name: `${RACE_NAME[race]} Warlord`, price: 250, race },
  { id: `av_${race}_f`, kind: 'avatar', name: `${RACE_NAME[race]} Matriarch`, price: 250, race },
]);

// --- Unit sets: reskin ALL of your units of that race in-match. `set_default` is the free, always-owned
// base look (equip it to revert to the original units); each race has a paid War Regalia skin. ---
export const DEFAULT_SET: CatalogItem = { id: 'set_default', kind: 'set', name: 'Standard Units', price: 0 };
export const SETS: readonly CatalogItem[] = RACE_KEYS.map((race): CatalogItem => ({
  id: `set_${race}`,
  kind: 'set',
  name: `${RACE_NAME[race]} War Regalia`,
  price: 500,
  race,
}));
/** All skin choices shown in the shop / profile: the free Default first, then the race sets. */
export const SETS_ALL: readonly CatalogItem[] = [DEFAULT_SET, ...SETS];

export const CATALOG: readonly CatalogItem[] = [...FRAMES, ...AVATARS, DEFAULT_SET, ...SETS];

const BY_ID: ReadonlyMap<string, CatalogItem> = new Map(CATALOG.map((it) => [it.id, it]));

export function catalogItem(id: string): CatalogItem | undefined {
  return BY_ID.get(id);
}

/** Ids owned by every player from the start (price 0): the default frame + the 5 race-default avatars. */
export const DEFAULT_UNLOCKS: readonly string[] = CATALOG.filter((it) => it.price === 0).map((it) => it.id);
/** Free avatars every profile starts with (the 5 race defaults). */
export const DEFAULT_AVATAR_IDS: readonly string[] = AVATARS.filter((it) => it.price === 0).map((it) => it.id);
/** Free frames every profile starts with (just the plain default). */
export const DEFAULT_FRAME_IDS: readonly string[] = FRAMES.filter((it) => it.price === 0).map((it) => it.id);

/** What a brand-new player has equipped: the plain frame, the Orc default avatar, and the default (base) skin. */
export const DEFAULT_EQUIPPED = { avatar: 'av_orc', frame: 'fr_default', skin: 'set_default' } as const;

// --- asset resolvers (UI layer uses these; kept here so ids and paths stay in one place) ---

/** The CSS class that paints a frame. Default frame has no ring (empty class). */
export function frameClass(id: string): string {
  return id === 'fr_default' ? '' : `frame--${id.replace(/^fr_/, '')}`;
}

/** The live canvas element for a premium `effect` frame, or undefined for flat/glow frames. */
export function frameEffect(id: string): FrameEffect | undefined {
  return BY_ID.get(id)?.effect;
}

/** The image src for an avatar. Defaults reuse the existing race head sprites; variants use new art. */
export function avatarSrc(id: string): string {
  const it = BY_ID.get(id);
  const race = it?.race ?? 'orc';
  // `av_<race>` -> existing head sprite; `av_<race>_m|f` -> generated portrait (path reserved for art)
  const variant = id.slice(`av_${race}`.length); // '' | '_m' | '_f'
  return variant ? `avatars/${race}${variant}.png` : `sprites/head_${race}.png`;
}
